import React, { useState, useEffect } from "react";
import {
  Box, Grid, Container, Typography, Card, CardContent,
  Avatar, Chip, CircularProgress,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../database/db";
import Page from "../components/Page";
import { AppWebsiteVisits, AppCurrentVisits } from "../components/_dashboard/app";

function StatCard({ title, value, icon, color, subtitle }) {
  return (
    <Card sx={{ height: "100%", borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 0.5 }}>{title}</Typography>
            <Typography variant="h3" sx={{ fontWeight: 700 }}>
              {value !== null && value !== undefined ? value : <CircularProgress size={22} />}
            </Typography>
            {subtitle && <Typography variant="caption" sx={{ color: "text.secondary" }}>{subtitle}</Typography>}
          </Box>
          <Avatar sx={{ bgcolor: color, width: 56, height: 56, borderRadius: 2 }}>
            <Icon icon={icon} width={28} height={28} color="#fff" />
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
}

function RecentActivityCard({ activities }) {
  return (
    <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)", height: "100%" }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Recent Sign-Ups</Typography>
        {activities.length === 0 && (
          <Typography variant="body2" color="text.secondary">No recent users</Typography>
        )}
        {activities.map((user, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
            <Avatar src={user.profileImage} sx={{ width: 36, height: 36, mr: 1.5 }}>
              {(user.name || user.username || "?").charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{user.name || user.username || "Unknown"}</Typography>
              <Typography variant="caption" color="text.secondary">{user.email || "—"}</Typography>
            </Box>
            <Chip
              label={user.brokerageConnected ? "Brokerage ✓" : "No Brokerage"}
              size="small"
              color={user.brokerageConnected ? "success" : "default"}
            />
          </Box>
        ))}
      </CardContent>
    </Card>
  );
}

export default function DashboardApp() {
  const [stats, setStats] = useState({
    totalUsers: null, activeUsers: null, passiveUsers: null,
    tokenRevenue: null, subRevenue: null, reports: null,
    fivAlerts: null, suspensions: null, brokerageConnected: null,
  });
  const [recentUsers, setRecentUsers] = useState([]);

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try {
      const usersSnap = await getDocs(collection(db, "User"));
      const users = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const total = users.length;
      const active = users.filter((u) => u.status === true || u.status === "active").length;
      const passive = total - active;
      const brokConnected = users.filter((u) => u.brokerageConnected).length;
      const sorted = [...users]
        .filter((u) => u.createdAt)
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
        .slice(0, 8);
      setRecentUsers(sorted);

      const reportsSnap = await getDocs(collection(db, "Reports"));
      const fivSnap = await getDocs(collection(db, "LegalArchive"));
      const tokenSnap = await getDocs(collection(db, "TokenTransactions"));
      const subSnap = await getDocs(collection(db, "Subscriptions"));
      const suspSnap = await getDocs(query(collection(db, "User"), where("suspended", "==", true)));

      const tokenRevenue = tokenSnap.docs.reduce((s, d) => s + (d.data().amount || 0), 0);
      const subRevenue = subSnap.docs.reduce((s, d) => s + (d.data().amount || 0), 0);

      setStats({
        totalUsers: total, activeUsers: active, passiveUsers: passive,
        brokerageConnected: brokConnected,
        tokenRevenue: tokenRevenue.toFixed(2),
        subRevenue: subRevenue.toFixed(2),
        reports: reportsSnap.size,
        fivAlerts: fivSnap.size,
        suspensions: suspSnap.size,
      });
    } catch (err) {
      console.error("Dashboard load error:", err);
    }
  };

  const cards = [
    { title: "Total Users", value: stats.totalUsers, icon: "fa-solid:users", color: "#FF6C40", subtitle: "All registered accounts" },
    { title: "Active Users", value: stats.activeUsers, icon: "eva:person-fill", color: "#00AB55", subtitle: "Currently enabled accounts" },
    { title: "Passive Users", value: stats.passiveUsers, icon: "eva:person-remove-fill", color: "#637381", subtitle: "Inactive / blocked accounts" },
    { title: "Brokerage Linked", value: stats.brokerageConnected, icon: "fa-solid:link", color: "#1890FF", subtitle: "Snap API connected users" },
    { title: "Token Revenue", value: stats.tokenRevenue !== null ? `$${stats.tokenRevenue}` : null, icon: "fa-solid:coins", color: "#FFC107", subtitle: "In-app token transactions" },
    { title: "Subscription Revenue", value: stats.subRevenue !== null ? `$${stats.subRevenue}` : null, icon: "fa-solid:credit-card", color: "#7635DC", subtitle: "Premium subscriptions" },
    { title: "Reported Users", value: stats.reports, icon: "octicon:database-16", color: "#FF4842", subtitle: "Community violations filed" },
    { title: "FIV Alerts", value: stats.fivAlerts, icon: "fa-solid:chart-line", color: "#FF6C40", subtitle: "Stocks >= 75% community owned" },
  ];

  return (
    <Page title="Dashboard | Fire Investing Admin">
      <Container maxWidth="xl">
        <Box sx={{ pb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Fire Investing Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">Real-time overview of your platform</Typography>
        </Box>
        <Grid container spacing={3}>
          {cards.map((card, i) => (
            <Grid key={i} item xs={12} sm={6} md={3}><StatCard {...card} /></Grid>
          ))}
          <Grid item xs={12} md={8}><AppWebsiteVisits /></Grid>
          <Grid item xs={12} md={4}><AppCurrentVisits /></Grid>
          <Grid item xs={12} md={8}><RecentActivityCard activities={recentUsers} /></Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)", height: "100%" }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Quick Stats</Typography>
                {[
                  { label: "Active Suspensions", value: stats.suspensions, color: "#FF4842" },
                  { label: "FIV Alerts Filed", value: stats.fivAlerts, color: "#FF6C40" },
                  { label: "Reports Pending", value: stats.reports, color: "#FFC107" },
                ].map((s, i) => (
                  <Box key={i} sx={{ display: "flex", justifyContent: "space-between", mb: 1.5, alignItems: "center" }}>
                    <Typography variant="body2">{s.label}</Typography>
                    <Chip label={s.value ?? "—"} size="small" sx={{ bgcolor: s.color, color: "#fff", fontWeight: 700 }} />
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
